<template>
  <div class="guide-getting-started">
    <p class="guide-lead">
      eSign Launchpad sends documents for legally binding electronic signature. The
      hosted portal handles single-signer ceremonies without any code. The REST API
      covers programmatic integrations at any volume. Both paths produce the same signed
      record, backed by the same audit trail, enforceable under the same statutes.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Who uses eSign Launchpad</h2>
    <p>
      Real estate teams close lease and purchase agreements. HR departments onboard
      employees and collect policy acknowledgments. Legal teams run contracts and NDAs
      through the platform and keep settlement paperwork audit-ready. Financial services
      firms disclose terms and capture account openings. Healthcare organizations handle
      patient consents and provider agreements. Sales teams close deals without the
      friction of email attachments and manual follow-up.
    </p>
    <p>
      Both non-technical users and developers are first-class. If you have never written
      code, sign into the portal, upload a document, add a signer, and send the package
      from a browser. If you write code, the REST API covers everything the portal does,
      and an MCP server exposes the same capabilities to AI agents.
    </p>

    <q-separator class="q-my-lg" />

    <h2>How it works</h2>
    <p>
      The platform is organized around a single object called a <em>package</em>. A
      package represents one signing transaction. Inside each package:
    </p>
    <ul>
      <li>One or more <strong>documents</strong>, the PDFs or Word files being signed.</li>
      <li>One or more <strong>signers</strong>, the people completing the package.</li>
      <li><strong>Signing fields</strong> that mark where each signer's action belongs on each page.</li>
      <li>Optional <strong>metadata</strong> like expiration, reminder cadence, and payment collection.</li>
    </ul>
    <p>
      When every signer finishes, the platform produces two artifacts. The first is a
      final signed PDF with every signature, initial, date, and filled field permanently
      applied to the document. The second is a Certificate of Completion, a short PDF
      summarizing who signed, when, from what IP address, through what authentication
      method, and at what UTC timestamp. Both are downloadable through the portal and
      the API, and the signed PDF is emailed to every signer automatically.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Legally binding signatures</h2>
    <p>
      Every signature the platform produces is enforceable under the U.S. ESIGN Act
      (Electronic Signatures in Global and National Commerce Act, 2000) and the Uniform
      Electronic Transactions Act (UETA). The platform implements every ceremony
      requirement those statutes prescribe:
    </p>
    <ul>
      <li>
        <strong>Intent to sign.</strong> Before the first signature, every signer
        accepts an explicit consent gate. Acceptance is recorded with IP address, user
        agent, UTC timestamp, and a SHA-256 hash of the exact disclosure shown.
      </li>
      <li>
        <strong>Consent to electronic records.</strong> The disclosure presented at the
        consent gate is ESIGN-compliant. It covers scope of consent, withdrawal
        procedures, hardware and software requirements, and how long records remain
        available.
      </li>
      <li>
        <strong>Signer identity verification.</strong> Five methods are built in: email
        delivery, access code, SMS one-time passcode, Knowledge-Based Authentication,
        and government-ID verification. Methods stack per signer for layered assurance.
      </li>
      <li>
        <strong>Tamper-evident audit trail.</strong> Every ceremony event is hash-chained
        across both the primary database and independent cloud blob storage. A public
        API endpoint verifies the chain and reports which links, if any, have been
        modified.
      </li>
      <li>
        <strong>Record retention.</strong> Signed documents remain retrievable for seven
        years from the date of signing, the horizon stated in the disclosure every
        signer accepts.
      </li>
      <li>
        <strong>Certificate of Completion.</strong> A PDF summary is generated for every
        completed package and can optionally be digitally signed with a platform PKI
        certificate.
      </li>
      <li>
        <strong>Copy to signer.</strong> Every signer receives an email with a download
        link to the final signed document as soon as the package completes.
      </li>
    </ul>
    <p>
      For a deeper look at the audit trail, certificate structure, and the full evidence
      pack contents, see
      <a href="/Docs/IntegrationGuides?topic=evidence">Evidence &amp; Compliance</a>.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Platform components</h2>
    <p>The same signing engine sits behind several complementary surfaces:</p>
    <ul>
      <li>
        <strong>Portal.</strong> The hosted web UI for creating packages, uploading
        documents, placing fields, managing signers, and tracking progress without code.
      </li>
      <li>
        <strong>REST API.</strong> Programmatic access to every portal capability at
        <code>api.esignlaunchpad.com</code>, authenticated via OAuth 2.0 bearer tokens
        (Personal Access Tokens or OAuth Client Credentials). See
        <a href="/Docs/IntegrationGuides?topic=authentication">Authentication &amp; Scopes</a>.
      </li>
      <li>
        <strong>Embedded signing.</strong> Drops the signing ceremony into your own
        application via iframe or redirect, bypassing the email invitation flow. See
        <a href="/Docs/IntegrationGuides?topic=embedded-signing">Embedded Signing</a>.
      </li>
      <li>
        <strong>Webhooks.</strong> Real-time server-to-server notifications for every
        lifecycle event, signed with HMAC-SHA256, with replay protection and automatic
        retry via a dead-letter queue. See
        <a href="/Docs/IntegrationGuides?topic=webhooks">Webhooks</a>.
      </li>
      <li>
        <strong>Templates.</strong> Reusable package setups so a standard NDA or offer
        letter is a single API call away. See
        <a href="/Docs/IntegrationGuides?topic=templates">Templates</a>.
      </li>
      <li>
        <strong>Signing Groups.</strong> Reusable recipient rosters for team sign-offs,
        letting any member of a named group fulfill a signing slot. See
        <a href="/Docs/IntegrationGuides?topic=signing-groups">Signing Groups</a>.
      </li>
      <li>
        <strong>MCP Server.</strong> Exposes the full API to AI agents like Claude and
        Cursor, with the tool catalog generated from the OpenAPI specification. See
        <a href="/Docs/IntegrationGuides?topic=ai-agents">AI Agents</a>.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>What sets eSign Launchpad apart</h2>

    <h3>Sign and collect payment in a single motion</h3>
    <p>
      Payment collection is fused into the signing ceremony rather than bolted on as a
      separate step. A signer can be charged during signing through the platform's
      Stripe Connect integration, with merchant revenue split and fee routing handled
      automatically. The signer experiences one flow, not two. Every subscription tier
      supports this, including Free.
    </p>

    <h3>Built for the AI era</h3>
    <p>
      eSign Launchpad ships a production-grade Model Context Protocol (MCP) server that
      generates over 150 tools from the platform's OpenAPI specification. AI agents like
      Claude, Cursor, and any MCP-compatible client can create packages, upload
      documents, place fields, add signers, send ceremonies, and track progress
      natively, without writing a custom API client. The server is published at
      <a href="https://www.npmjs.com/package/@esignlaunchpad/mcp-server" target="_blank" rel="noopener">@esignlaunchpad/mcp-server</a>
      on npm.
    </p>

    <h3>Accessible at every scale</h3>
    <p>
      The Free tier has no monthly cost. An individual can send their first signed
      document today with no subscription or credit card on file, paying only a small
      per-package fee when they send. The Business tier adds full API access, webhooks,
      custom branding, and enterprise features. There is no per-seat or per-sender
      pricing, so you grow into what you actually use.
    </p>

    <h3>Drop-in migration from DocuSign or Adobe Sign</h3>
    <p>
      Existing documents with DocuSign-style tags (<code>\s1\</code>, <code>/sn1/</code>)
      or Adobe Sign-style tags (<code v-pre>{{Sig_es_:signer1:signature}}</code>) work
      unchanged. The platform auto-detects the tag format in uploaded documents and
      converts to its native placement model, so you keep your templates and only change
      the backend. See <a href="/Docs/IntegrationGuides?topic=text-tags">Text Tags</a>.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Security and trust</h2>
    <ul>
      <li>
        Data at rest uses AES-256 encryption. Every request travels over TLS 1.2 or
        higher.
      </li>
      <li>
        Multi-tenant isolation runs at the service layer. A tenant's data is never
        visible to another by design.
      </li>
      <li>
        API authentication uses OAuth 2.0 with two credential types: long-lived
        Personal Access Tokens (signed JWTs) and OAuth Client Credentials
        (<code>client_id</code> + <code>client_secret</code> exchanged at
        <code>/connect/token</code>). The plaintext credential appears once at generation
        time and is never stored server-side. Tokens are validated against our published
        JWKS at <code>/.well-known/jwks</code>.
      </li>
      <li>
        Webhook deliveries are signed with HMAC-SHA256 over the request body and a Unix
        timestamp, which defeats replay attacks.
      </li>
      <li>
        Audit events are immutable once written. Tampering is detectable via the dual
        hash-chain verification endpoint.
      </li>
      <li>
        Signer authentication routes through vetted third-party providers: Twilio for
        SMS, Authenticate.com for KBA, and Persona for government-ID verification.
        Credentials never touch the platform directly.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>Where to go next</h2>
    <ul>
      <li>
        New to e-signatures or evaluating the platform? Continue through the Knowledge
        Base in order, starting with
        <a href="/Docs/IntegrationGuides?topic=quick-start">Quick Start</a>.
      </li>
      <li>
        Integrating the API? Start with
        <a href="/Docs/IntegrationGuides?topic=authentication">Authentication &amp; Scopes</a>,
        then <a href="/Docs/IntegrationGuides?topic=packages">Packages</a>, then
        <a href="/Docs/IntegrationGuides?topic=quick-start">Quick Start</a> for an
        end-to-end example.
      </li>
      <li>
        Embedding the ceremony in your application? See
        <a href="/Docs/IntegrationGuides?topic=embedded-signing">Embedded Signing</a>.
      </li>
      <li>
        Evaluating legal defensibility? See
        <a href="/Docs/IntegrationGuides?topic=evidence">Evidence &amp; Compliance</a>
        alongside <a href="/Docs/IntegrationGuides?topic=signers">Signers</a> for
        authentication detail.
      </li>
      <li>
        Want the full endpoint catalog with try-it-out examples? Open the
        <a href="/Docs/ApiReference">API Reference</a>.
      </li>
    </ul>
  </div>
</template>

<script>
export default {
    name: 'GuideGettingStarted'
};
</script>

<style scoped>
.guide-lead {
    font-size: 1.1rem;
    line-height: 1.7;
    color: #334155;
}
</style>
